import React, { useEffect, ChangeEvent } from 'react';
import { useLocation } from 'react-router';
import { useUser } from '@context/user';
import { useHistory } from 'react-router-dom';
import { clientLogin } from '@services/client';
import { useMutation } from 'react-query';
import { ProfileOptions } from './ProfileOptions';
import { PresentationTypeInput } from './PresentationTypeInput';
import { PAGE_URLS, PresentationType, Profile } from '@constants';
import { LoginStyle, LoginInput, LoginButton, LoginLabel, PresentationTypeContainer, InputContainer, LoadingButton } from './styles';
import { checkForCreditAnalysisQueryParams } from '@utils/creditAnalysisHelpers';
import { handleLoginSuccess } from '@utils/login';
import { getErrorMessage } from '@utils/errorHandling';
import { isValidNumber } from '@utils/validateInputs';

// Exporting for tests.
export const creditAnalysisHtmlError = `
    <html>
        <head>
            <title>Server Error</title>
        </head>
        <body>
            <font color="#aa0000">
                <h2>Server Error.</h2>
            </font>
            There was an unexpected error in the request processing.
        </body>
    </html>
`;

// LeadId should be a string - for input text only 
export type LoginInfo = {
    presentationType: string,
    profile: string,
    email: string,
    leadId: string,
    salesRepId: number
};

type ReactQueryError = {
    response?: {
        status: number,
        data: string
    },
    message: string,
    status: number
}

export const ClientLogin = () => {
    const history = useHistory();
    const user = useUser();
    const { search } = useLocation();
    const { ADMIN, CUSTOMER_SERVICE } = Profile;
    const { SLIDES_PRESENTATION, DOCUMENTS_PRESENTATION } = PresentationType;
    const { ADMIN_PAGE } = PAGE_URLS;
    const [message, setMessage] = React.useState('');
    const [loginInfo, setLoginInfo] = React.useState<LoginInfo>({
        presentationType: SLIDES_PRESENTATION,
        profile: user.profile,
        email: '',
        leadId: '',
        salesRepId: user.id
    });

    useEffect(() => {
        checkForCreditAnalysisQueryParams(search, loginInfo, setLoginInfo);
    }, []);

    const mutation = useMutation((loginInfo: LoginInfo) => clientLogin(loginInfo), {
        onError: (error: ReactQueryError) => {
            const errorMsg = getErrorMessage(error);
            const errorStatus = error.response?.status || error.status;

            // To-do: handle any errors with <html> in the message?
            if (errorMsg === creditAnalysisHtmlError) {
                setMessage('Encountered an unusual error while trying to retrieve client data from Credit Analysis. Please alert your manager');
            } else if (errorMsg !== creditAnalysisHtmlError && errorStatus === 500) {
                setMessage('A server error occurred. Please try again, and refresh the page if you still experience issues.');
            } else {
                setMessage(errorMsg);
            }
        },
        onSuccess: async () => await handleLoginSuccess(loginInfo, history)
    });

    const handleChange = (event: ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
        const { name, value } = event.target;

        setLoginInfo(loginInfo => ({ ...loginInfo, [name]: value }));
    };

    // To-do: validate email, incorporate toast
    const handleSubmit = (event: ChangeEvent<HTMLButtonElement>) => {
        event.preventDefault();
        const { profile } = loginInfo;

        if (profile === ADMIN) {
            history.push(ADMIN_PAGE);
        } else {
            const { email, leadId } = loginInfo;

            if (!leadId && profile === CUSTOMER_SERVICE) {
                setMessage('Please fill in the leadId field');
            } else if ((!email || !leadId) && profile !== CUSTOMER_SERVICE) {
                setMessage('Please fill in the email and leadId fields');
            } else if (!validateLeadId(leadId)) {
                setMessage('Please enter a valid integer for the leadId field');
            } else {
                mutation.mutate(loginInfo);
            }
        }
    };

    function validateLeadId(leadId) {
        if (!isValidNumber(leadId) || leadId.toString().includes('.')) {
            return false;
        }

        return true;
    }

    const { profile, email, leadId, presentationType } = loginInfo;

    return (
        <LoginStyle>
            <PresentationTypeContainer>
                <PresentationTypeInput 
                    currentPresentationType={presentationType}
                    profile={profile}
                    setLoginInfo={setLoginInfo}
                    presentationType={SLIDES_PRESENTATION}/>
                 <PresentationTypeInput 
                    currentPresentationType={presentationType}
                    profile={profile}
                    setLoginInfo={setLoginInfo}
                    presentationType={DOCUMENTS_PRESENTATION}/>
            </PresentationTypeContainer>
            <InputContainer>
                <LoginLabel htmlFor='profile'>
                    PROFILE
                </LoginLabel>
                <select
                    id='profile'
                    name='profile'
                    placeholder='Select'
                    value={profile}
                    onChange={handleChange}>
                      <ProfileOptions profile={user.profile} />
                </select>
            </InputContainer>
            <InputContainer>
                <LoginLabel htmlFor='client-email'>
                    CLIENT EMAIL
                </LoginLabel>
                <LoginInput
                    id='client-email'
                    type='text'
                    name='email'
                    disabled={profile === ADMIN || profile === CUSTOMER_SERVICE}
                    value={email} 
                    placeholder='Enter client email'
                    onChange={handleChange}
                />
            </InputContainer>
            <InputContainer>
                <LoginLabel htmlFor='lead-id'>
                    LEAD ID
                </LoginLabel>
                <LoginInput
                    id='lead-id'
                    name='leadId'
                    type='number'
                    disabled={profile === ADMIN}
                    placeholder='Lead ID'
                    value={leadId}
                    onChange={handleChange}
                />
            </InputContainer>
            { mutation.isLoading ? (
                <LoadingButton>
                    LOADING...
                </LoadingButton>
            ) : (
                <LoginButton type='submit' onClick={handleSubmit}>
                    SUBMIT
                </LoginButton>
            )}
            <div id='validation'>{message}</div>
        </LoginStyle>
    );
};

export default ClientLogin;
